import type { Cliente, LancamentoComissao, Pedido, SubPedido, TabelaPreco } from '../db/tipos'
import { valorEfetivo } from './motor'
import { mesDaComissao, valorConferido } from './conferencia'
import { letrasPorMarca } from '../pedidos/agrupamento'
import { indiceDaLetra } from '../pedidos/numeracao'
import { converterNumeroBR, normalizarTexto } from '../lib/textoBR'

// ▶ As linhas da tela Baixa de Comissao (spec 2026-10-04): montar, buscar,
// filtrar, ordenar. Puro. A conta da baixa continua em conferencia.ts.

export type LinhaConferencia = {
  id: string
  lancamento: LancamentoComissao
  pedido: string // '2026-0026-B' ou '…'
  rotulo: string // `${pedido} · ${tabela}` ou pedido -- o nome acessivel de hoje
  data: string // data do pedido 'AAAA-MM-DD' ou ''
  tabelaId: string | null
  tabela: string | null
  clienteId: string | null
  cliente: string | null
  devido: number
  valor: number // o que a linha mostra (conferido / recebido)
  ajuste: boolean // valor != devido
  mesOrigem: string | null
}

export type Contexto = { subPedidos: SubPedido[]; pedidos: Pedido[]; tabelas: TabelaPreco[]; clientes: Cliente[] }

const CENTAVO = 0.004

export function montarLinhas(
  lancamentos: LancamentoComissao[], ctx: Contexto, valorDe: (l: LancamentoComissao) => number,
): LinhaConferencia[] {
  return lancamentos.map((l) => {
    const s = ctx.subPedidos.find((x) => x.id === l.sub_pedido_id)
    const p = s ? ctx.pedidos.find((x) => x.id === s.pedido_id) : undefined
    const t = s?.tabela_preco_id ? ctx.tabelas.find((x) => x.id === s.tabela_preco_id) : undefined
    const c = p ? ctx.clientes.find((x) => x.id === p.cliente_id) : undefined
    // Mesma letra do rotuloDoPedido de antes: irmaos vivos ordenados pela letra.
    const irmaos = s
      ? ctx.subPedidos
        .filter((x) => x.pedido_id === s.pedido_id && x.apagado_em === null)
        .sort((a, b) => indiceDaLetra(a.letra) - indiceDaLetra(b.letra))
      : []
    const letra = s ? letrasPorMarca(irmaos).get(s.representada_id) : undefined
    const pedido = p && letra ? `${p.numero}-${letra}` : '…'
    const devido = valorEfetivo(l)
    const valor = valorDe(l)
    return {
      id: l.id, lancamento: l, pedido, rotulo: t ? `${pedido} · ${t.nome}` : pedido, data: p?.data ?? '',
      tabelaId: t?.id ?? null, tabela: t?.nome ?? null,
      clienteId: c?.id ?? null, cliente: c ? c.nome_fantasia || c.razao_social : null,
      devido, valor, ajuste: Math.abs(valor - devido) > CENTAVO, mesOrigem: mesDaComissao(l),
    }
  })
}

export type Filtros = {
  situacao: 'todas' | 'normal' | 'ajuste'
  soAtrasadas: boolean
  clienteId: string | null
  tabelaId: string | null
}
export const FILTROS_VAZIOS: Filtros = { situacao: 'todas', soAtrasadas: false, clienteId: null, tabelaId: null }

export function contarFiltros(f: Filtros): number {
  return (f.situacao !== 'todas' ? 1 : 0) + (f.soAtrasadas ? 1 : 0) + (f.clienteId ? 1 : 0) + (f.tabelaId ? 1 : 0)
}

export function filtrarLinhas(
  linhas: LinhaConferencia[], f: Filtros, busca: string, atrasadas: Set<string>,
): LinhaConferencia[] {
  const termo = normalizarTexto(busca.trim())
  return linhas.filter((l) =>
    (f.situacao === 'todas' || (f.situacao === 'ajuste') === l.ajuste) &&
    (!f.soAtrasadas || atrasadas.has(l.id)) &&
    (!f.clienteId || l.clienteId === f.clienteId) &&
    (!f.tabelaId || l.tabelaId === f.tabelaId) &&
    (termo === '' || normalizarTexto(`${l.pedido} ${l.tabela ?? ''} ${l.cliente ?? ''}`).includes(termo)))
}

export type Ordem = { coluna: 'data' | 'pedido' | 'descricao' | 'valor'; sentido: 'asc' | 'desc' }
export const ORDEM_PADRAO: Ordem = { coluna: 'data', sentido: 'asc' }

export function ordenarLinhas(linhas: LinhaConferencia[], o: Ordem): LinhaConferencia[] {
  const chave = (l: LinhaConferencia): string | number =>
    o.coluna === 'valor' ? l.valor
      : o.coluna === 'pedido' ? l.pedido
        : o.coluna === 'descricao' ? `${l.tabela ?? ''} ${l.cliente ?? ''}` : l.data
  const sinal = o.sentido === 'asc' ? 1 : -1
  return [...linhas].sort((a, b) => {
    const x = chave(a)
    const y = chave(b)
    const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'pt-BR')
    return cmp * sinal || a.pedido.localeCompare(b.pedido)
  })
}

export function proximaOrdem(atual: Ordem, coluna: Ordem['coluna']): Ordem {
  return atual.coluna === coluna
    ? { coluna, sentido: atual.sentido === 'asc' ? 'desc' : 'asc' }
    : { coluna, sentido: 'asc' }
}

type Opcao = { id: string; nome: string }

export function opcoesDeFiltro(linhas: LinhaConferencia[]): { clientes: Opcao[]; tabelas: Opcao[] } {
  const unicos = (pares: Array<[string | null, string | null]>): Opcao[] =>
    [...new Map(pares.filter((p): p is [string, string] => p[0] !== null && p[1] !== null)).entries()]
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  return {
    clientes: unicos(linhas.map((l) => [l.clienteId, l.cliente])),
    tabelas: unicos(linhas.map((l) => [l.tabelaId, l.tabela])),
  }
}

// "Marcar todos" age so nas VISIVEIS; a tela guarda quem ela DESmarcou.
export function marcarVisiveis(desmarcados: Set<string>, visiveis: string[], marcar: boolean): Set<string> {
  const novo = new Set(desmarcados)
  for (const id of visiveis) {
    if (marcar) novo.delete(id)
    else novo.add(id)
  }
  return novo
}

// Filtro nao desmarca: o rodape avisa quantas marcadas estao escondidas.
export function marcadasForaDoFiltro(conferiveis: string[], desmarcados: Set<string>, visiveis: Set<string>): number {
  return conferiveis.filter((id) => !desmarcados.has(id) && !visiveis.has(id)).length
}

export function mesesComComissao(lancamentos: LancamentoComissao[], mesAberto: string): string[] {
  const meses = new Set<string>([mesAberto])
  for (const l of lancamentos) {
    const m = mesDaComissao(l)
    if (m) meses.add(m)
  }
  return [...meses].sort().reverse()
}

// Revisao final 04/10: "So atrasadas" so existe na aba A receber, e "Situacao"
// nao tem sentido em Debitos/Creditos (la toda linha vale 0). Carregados para
// outra aba, escondiam TODAS as linhas -- inclusive os botoes "Pago".
export function filtrosDaAba(f: Filtros, aba: 'a-receber' | 'recebidas' | 'previstas' | 'pendencias'): Filtros {
  if (aba === 'a-receber') return f
  return { ...f, soAtrasadas: false, situacao: aba === 'pendencias' ? 'todas' : f.situacao }
}

// Salvar a janela so devolve a linha a baixa quando o valor conferido MUDOU:
// abrir uma linha que ela desmarcou ("nao veio") e salvar sem mexer nao pode
// remarca-la calado.
export function remarcaAoSalvar(textoAntes: string | undefined, textoNovo: string | undefined, devido: number): boolean {
  return valorConferido(devido, textoAntes) !== valorConferido(devido, textoNovo)
}

// Minor 04/10, item 4: o texto que nao vira numero. A janela nao deixa salvar
// com ele -- salvo, ele gravava o devido e o aviso "nao entendi" sumia ao fechar.
// O vazio NAO conta: vazio e "voltar ao devido".
export function textoIlegivel(texto: string): boolean {
  if (texto === '') return false
  const convertido = converterNumeroBR(texto)
  return convertido === null || convertido < 0
}

// Minor 04/10, item 5: so guarda o texto quando ele MUDA o valor. Salvar a
// janela sem mexer guardava "40,00" e a troca de fornecedora perguntava
// "Descartar?" sem ter nada a descartar.
export function textoParaGuardar(texto: string | undefined, devido: number): string | undefined {
  if (texto === undefined || texto === '') return undefined
  return valorConferido(devido, texto) === devido ? undefined : texto
}
